import { Link, StatusIndicator } from '@cloudscape-design/components';
import FilterDatePicker, {
  createDateFilterConfig,
} from 'src/components/filter/FilterDatePicker';
import {
  AUDIT_STATUS_LABELS,
  AUDIT_TYPE_LABELS,
  AuditTypeOptions,
  SCHDULE_TYPE_LABELS,
  ScheduleType,
  ScheduleStatus,
  SCHEDULE_STATUS_LABELS,
} from 'src/contstant/commonLabels';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import { FilterItemType } from 'src/hooks/useGenericFilter';
import { AuditSchedule } from 'src/types/helios/AuditSchedule';
import { formatDateTime } from 'src/utils/generalUtils';
import { ExportFileConfig } from 'src/utils/exportFileUtils';

export enum SchedulesPropertyKey {
  SCHEDULE_ACTIVE_STATUS = 'parent.isActive',
  SCHEDULE_EXECUTION_STATUS = 'parent.status',
  AUDIT_TYPE = 'parent.auditType',
  SCHEDULE_TYPE = 'parent.scheduleType',
  LAST_UPDATED_AT = 'parent.lastUpdatedAt',
  CREATED_AT = 'parent.createdAt',
}

// Default visible columns
export const defaultVisibleColumns = [
  'scheduleId',
  'name',
  'auditType',
  'scheduleType',
  'nextExecutionDate',
  'status',
  'isActive',
];

// Available filter options for UI
export const filterOptions = [
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_ACTIVE_STATUS,
    value: 'true',
    label: 'Active',
  },
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_ACTIVE_STATUS,
    value: 'false',
    label: 'Inactive',
  },
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_EXECUTION_STATUS,
    value: ScheduleStatus.SCHEDULED,
    label: SCHEDULE_STATUS_LABELS[ScheduleStatus.SCHEDULED],
  },
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_EXECUTION_STATUS,
    value: ScheduleStatus.IN_PROGRESS,
    label: SCHEDULE_STATUS_LABELS[ScheduleStatus.IN_PROGRESS],
  },
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_EXECUTION_STATUS,
    value: ScheduleStatus.COMPLETED,
    label: SCHEDULE_STATUS_LABELS[ScheduleStatus.COMPLETED],
  },
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_EXECUTION_STATUS,
    value: ScheduleStatus.PAUSED,
    label: SCHEDULE_STATUS_LABELS[ScheduleStatus.PAUSED],
  },
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_EXECUTION_STATUS,
    value: ScheduleStatus.INACTIVE,
    label: SCHEDULE_STATUS_LABELS[ScheduleStatus.INACTIVE],
  },
  {
    propertyKey: SchedulesPropertyKey.AUDIT_TYPE,
    value: AuditTypeOptions.CALIBRATION,
    label: AUDIT_TYPE_LABELS[AuditTypeOptions.CALIBRATION],
  },
  {
    propertyKey: SchedulesPropertyKey.AUDIT_TYPE,
    value: AuditTypeOptions.PROGRESS,
    label: AUDIT_TYPE_LABELS[AuditTypeOptions.PROGRESS],
  },
  {
    propertyKey: SchedulesPropertyKey.AUDIT_TYPE,
    value: AuditTypeOptions.SITUATION,
    label: AUDIT_TYPE_LABELS[AuditTypeOptions.SITUATION],
  },
  {
    propertyKey: SchedulesPropertyKey.AUDIT_TYPE,
    value: AuditTypeOptions.TEST,
    label: AUDIT_TYPE_LABELS[AuditTypeOptions.TEST],
  },
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_TYPE,
    value: ScheduleType.ONCE,
    label: SCHDULE_TYPE_LABELS[ScheduleType.ONCE as keyof typeof SCHDULE_TYPE_LABELS],
  },
  {
    propertyKey: SchedulesPropertyKey.SCHEDULE_TYPE,
    value: ScheduleType.RECURRING,
    label:
      SCHDULE_TYPE_LABELS[ScheduleType.RECURRING as keyof typeof SCHDULE_TYPE_LABELS],
  },
];

// Default query applied on page load - show only active schedules and exclude completed ones
export const defaultQuery = {
  tokens: [
    {
      propertyKey: SchedulesPropertyKey.SCHEDULE_ACTIVE_STATUS,
      value: 'true',
      operator: '=',
    },
    {
      propertyKey: SchedulesPropertyKey.SCHEDULE_EXECUTION_STATUS,
      value: ScheduleStatus.COMPLETED,
      operator: '!=',
    },
  ],
  operation: 'and' as const,
};

export const getItemValue = (item: AuditSchedule, propertyKey: string): any => {
  if (!item) return '';

  switch (propertyKey) {
    case SchedulesPropertyKey.SCHEDULE_ACTIVE_STATUS:
      return {
        itemValue: item?.isActive,
        type: FilterItemType.BOOLEAN,
      };
    case SchedulesPropertyKey.SCHEDULE_EXECUTION_STATUS:
      return {
        itemValue: item?.status,
        type: FilterItemType.TEXT,
      };
    case SchedulesPropertyKey.AUDIT_TYPE:
      return {
        itemValue: item?.auditType,
        type: FilterItemType.TEXT,
      };
    case SchedulesPropertyKey.SCHEDULE_TYPE:
      return {
        itemValue: item?.scheduleType,
        type: FilterItemType.TEXT,
      };
    case SchedulesPropertyKey.LAST_UPDATED_AT:
      return {
        itemValue: item?.lastUpdatedAt,
        type: FilterItemType.DATE,
      };
    case SchedulesPropertyKey.CREATED_AT:
      return {
        itemValue: item?.createdAt,
        type: FilterItemType.DATE,
      };
    default:
      return { itemValue: '' };
  }
};

export const getSearchableFields = (item: AuditSchedule) => [
  item.name || '',
  item.description || '',
  item.createdBy || '',
  item.lastUpdatedBy || '',
];

export const getFilterSchedulesFilterProperties = (): any[] => {
  const baseFilters: any[] = [
    {
      key: SchedulesPropertyKey.SCHEDULE_ACTIVE_STATUS,
      propertyLabel: 'Active Status',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Active values',
    },
    {
      key: SchedulesPropertyKey.SCHEDULE_EXECUTION_STATUS,
      propertyLabel: 'Execution Status',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Execution Status values',
    },
    {
      key: SchedulesPropertyKey.AUDIT_TYPE,
      propertyLabel: 'Audit Type',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Audit Type values',
    },
    {
      key: SchedulesPropertyKey.SCHEDULE_TYPE,
      propertyLabel: 'Schedule Type',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Schedule Type values',
    },
    createDateFilterConfig({
      key: SchedulesPropertyKey.LAST_UPDATED_AT,
      propertyLabel: 'Updated At',
      groupValuesLabel: 'Date',
    }),
    createDateFilterConfig({
      key: SchedulesPropertyKey.CREATED_AT,
      propertyLabel: 'Created At',
      groupValuesLabel: 'Date',
    }),
  ];

  return baseFilters;
};

export const getSchedulesTableDefinition = () => {
  return [
    {
      id: 'scheduleId',
      header: 'Schedule ID',
      cell: (item: AuditSchedule) => item.scheduleId,
    },
    {
      id: 'name',
      header: 'Name',
      cell: (item: AuditSchedule) => <Link>{item.name}</Link>,
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item: AuditSchedule) => item.description,
    },
    {
      id: 'auditType',
      header: 'Audit Type',
      cell: (item: AuditSchedule) =>
        AUDIT_TYPE_LABELS[item.auditType as keyof typeof AUDIT_TYPE_LABELS],
    },
    {
      id: 'scheduleType',
      header: 'Schedule Type',
      cell: (item: AuditSchedule) =>
        SCHDULE_TYPE_LABELS[item.scheduleType as keyof typeof SCHDULE_TYPE_LABELS],
    },
    {
      id: 'nextExecutionDate',
      header: 'Next Execution',
      cell: (item: AuditSchedule) =>
        formatDateTime(item.nextExecutionDate, 'DD-MMM-YYYY'),
    },
    {
      id: 'status',
      header: 'Execution Status',
      cell: (item: AuditSchedule) => {
        const status = item.status;
        const statusType =
          status === ScheduleStatus.COMPLETED
            ? 'success'
            : status === ScheduleStatus.IN_PROGRESS
              ? 'in-progress'
              : status === ScheduleStatus.PAUSED
                ? 'stopped'
                : status === ScheduleStatus.INACTIVE
                  ? 'stopped'
                  : 'pending';
        return (
          <StatusIndicator type={statusType}>
            {SCHEDULE_STATUS_LABELS[status as keyof typeof SCHEDULE_STATUS_LABELS] ||
              status}
          </StatusIndicator>
        );
      },
    },
    {
      id: 'isActive',
      header: 'Active Status',
      cell: (item: AuditSchedule) => {
        const isActive = item.isActive;
        return (
          <StatusIndicator type={isActive ? 'success' : 'error'}>
            {isActive ? 'Active' : 'Inactive'}
          </StatusIndicator>
        );
      },
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: AuditSchedule) => item.createdBy || '-',
    },
    {
      id: 'createdAt',
      header: 'Created At',
      cell: (item: AuditSchedule) => formatDateTime(item.createdAt),
    },
    {
      id: 'lastUpdatedBy',
      header: 'Updated By',
      cell: (item: AuditSchedule) => item.lastUpdatedBy || '-',
    },
    {
      id: 'lastUpdatedAt',
      header: 'Updated At',
      cell: (item: AuditSchedule) => formatDateTime(item.lastUpdatedAt),
    },
  ];
};

export const exportSchedulesConfig: ExportFileConfig = {
  fileName: 'schedules_list.csv',
  mappings: {
    'Schedule ID': (schedule: AuditSchedule) => schedule.scheduleId,
    'Name': (schedule: AuditSchedule) => schedule.name,
    'Description': (schedule: AuditSchedule) => schedule.description,
    'Audit Type': (schedule: AuditSchedule) =>
      AUDIT_TYPE_LABELS[schedule.auditType as keyof typeof AUDIT_TYPE_LABELS],
    'Schedule Type': (schedule: AuditSchedule) => schedule.scheduleType,

    'Business Unit': (schedule: AuditSchedule) => schedule.businessUnit,
    'Program': (schedule: AuditSchedule) => schedule.program,
    'Next Execution': (schedule: AuditSchedule) => {
      if (!schedule.nextExecutionDate) return '-';

      return formatDateTime(schedule.nextExecutionDate, 'DD-MMM-YYYY');
    },
    'Execution Status': (schedule: AuditSchedule) =>
      SCHEDULE_STATUS_LABELS[schedule.status as keyof typeof SCHEDULE_STATUS_LABELS] ||
      schedule.status ||
      '-',
    'Active Status': (schedule: AuditSchedule) =>
      schedule.isActive ? 'Active' : 'Inactive',
    'Created By': (schedule: AuditSchedule) => schedule.createdBy || '-',
    'Created At': (schedule: AuditSchedule) => {
      if (!schedule.createdAt) return '-';

      return formatDateTime(schedule.createdAt);
    },
    'Updated By': (schedule: AuditSchedule) => schedule.lastUpdatedBy || '-',
    'Updated At': (schedule: AuditSchedule) => {
      if (!schedule.lastUpdatedAt) return '-';

      return formatDateTime(schedule.lastUpdatedAt);
    },
  },
};
